import { Overlay } from '@angular/cdk/overlay';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { BaseAutoCompleteComponent } from '../../../base-auto-complete.component';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { tap, debounceTime, distinctUntilChanged, switchMap, of, Subject, takeUntil } from 'rxjs';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { CLEAR, FOR, INSERT_LINE_NUMBER_AND_CITY, NO_LINES_FOUND } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Direction } from '@angular/cdk/bidi';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import {
  createSlideUpListboxId,
  SlideUpComboboxListKeyboard,
} from '../../../shared/slide-up-combobox-list-keyboard.controller';

@Component({
    selector: 'app-line-auto-complete',
    templateUrl: './line-auto-complete.component.html',
    styleUrls: ['./line-auto-complete.component.scss'],
    standalone: false
})
export class LineAutoCompleteComponent extends BaseAutoCompleteComponent<Route> implements OnInit {

  private destroy$ = new Subject<void>();

  noResultsText: string
  clearText: string
  forText: string
  inputPlaceholder: string
  iconName: string

  direction: Direction

  readonly listboxId = createSlideUpListboxId('mot-lines-listbox');
  currentResults: Route[] = [];
  readonly comboboxKeyboard: SlideUpComboboxListKeyboard;

  override autoCompleteMinChar = 1;

  @ViewChild('input', { read: ElementRef })
  private searchInputHost?: ElementRef<HTMLElement>;

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,
    favoritesService: FavoritesService,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: any,

    private readonly autoCompleteService: AutoCompleteService,
    private readonly languageService: LanguageService,
    private readonly modalService: ModalService

  ) {
    super(
      favoritesService,
      routePlanningService,
      resizeService,
      overlay
    );
    this.comboboxKeyboard = new SlideUpComboboxListKeyboard(
      this.listboxId,
      () => this.currentResults,
      () => this.isListboxExpanded,
      () => this.searchInputHost?.nativeElement,
      (index) =>
        this.chooseValue(
          this.currentResults[index],
          !!this.data.saveResultToStorage
        )
    );
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.initializeAddressFormControl();
    this.initializeMultiLanguageTexts()
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction
      this.noResultsText = NO_LINES_FOUND[language.language]
      this.forText = FOR[language.language]
      this.clearText = CLEAR[language.language]
      this.inputPlaceholder = INSERT_LINE_NUMBER_AND_CITY[language.language]
      this.iconName = language.direction === "rtl" ? "chevron-right" : "chevron-left"

    })
  }

  private initializeAddressFormControl() {
    if (!!this.data?.line) {
      this.formControl.setValue(
        this.data.line.routeName,
        { emitEvent: false }
      );
    }

    this.formControl.valueChanges
      .pipe(
        tap(() => {
          this.emptyResults$.next(false);
          this.comboboxKeyboard.resetHighlight();
        }),
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        switchMap((val) => this.autoCompleteService.searchLines(val)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.results$ = of(results);
        this.currentResults = (results || []).map((r) => r.value);
        this.emptyResults$.next(results.length === 0);
        this.comboboxKeyboard.resetHighlight();
      });
  }

  getOptionId(index: number): string {
    return this.comboboxKeyboard.getOptionId(index);
  }

  get activeOptionId(): string | null {
    return this.comboboxKeyboard.activeOptionId;
  }

  get isListboxExpanded(): boolean {
    return (
      !!this.formControl.value &&
      this.formControl.value.length >= this.autoCompleteMinChar &&
      this.currentResults.length > 0
    );
  }

  get activeOptionIndex(): number {
    return this.comboboxKeyboard.activeOptionIndex;
  }

  onComboboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onComboboxKeydown(event);
  }

  onListboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onListboxKeydown(event);
  }

  onListboxContainerFocus(event: FocusEvent): void {
    this.comboboxKeyboard.onListboxContainerFocus(event);
  }

  onOptionFocus(index: number): void {
    this.comboboxKeyboard.onOptionFocus(index);
  }

  override clearValue() {
    this.comboboxKeyboard.resetHighlight();
    this.currentResults = [];
    super.clearValue();
  }

  protected override insertChosenValueToStorage(route: Route) {
    let favoriteLines = this.favoritesService.favorites?.lines;

    if (!favoriteLines?.lastSearches) {
      favoriteLines = {
        ...favoriteLines,
        type: eFavoriteType.LINES,
        lastSearches: [],
      };
    }

    if (
      favoriteLines.lastSearches.length >= this.FEED_LAST_SEARCHES_AMOUNT
    ) {
      favoriteLines.lastSearches.pop();
    }

    if (
      !favoriteLines.lastSearches.find(
        (search) => search.routeId === route.routeId
      )
    ) {
      favoriteLines.lastSearches.unshift({
        ...route,
        type: eFavoriteType.LAST_SEARCH,
      });
    }

    this.favoritesService.updateFavorite(favoriteLines);
  }

  openAgencyContact(isForOrderSeat: boolean, agencyId: number) {
    this.modalService.openAgencyContactInfoModal(agencyId, isForOrderSeat)
  }
}
